import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, picUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Users, TrendingUp, Coins, ArrowLeft, MessageCircle,
  Trophy, Loader2, Share2, Crown, Flame,
} from "lucide-react";

/**
 * "Signup Downline" — every user who joined via one of MY shared video
 * links, ranked by lifetime commission I've earned from their boosts.
 * The commission is 10% of every boost gift their videos receive, paid
 * automatically. Powers a leaderboard-style dashboard so I can spot the
 * hot creators in my downline and DM them to keep posting.
 */
function fmtUsd(cents) {
  const d = (Number(cents) || 0) / 100;
  return `$${d.toFixed(2)}`;
}
function fmtCoins(cents) {
  const n = Math.max(0, Number(cents) || 0);
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return `${n}`;
}
function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch { return ""; }
}

export default function MySignups() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    api
      .get("/me/signups")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6 text-center">
        <Users className="h-10 w-10 text-[#39FF14] mb-4" strokeWidth={1.5} />
        <p className="font-display text-2xl font-bold mb-2">Sign in to see your downline</p>
        <p className="text-white/50 text-sm">Track everyone who joined through your shared videos.</p>
      </div>
    );
  }

  const totals = data?.totals || {
    signup_count: 0,
    commission_lifetime_cents: 0,
    commission_last_30d_cents: 0,
    active_earners: 0,
  };
  const signups = data?.signups || [];

  const shareUrl = `${window.location.origin}/u/${user.username}`;
  const copyProfileLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-24">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* ------- Header ------- */}
        <div>
          <Link
            to="/earnings"
            data-testid="signups-back"
            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-white/40 hover:text-white/70 mb-2"
          >
            <ArrowLeft className="h-3 w-3" /> earnings
          </Link>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-[#39FF14]/20 border border-[#39FF14]/40 flex items-center justify-center">
              <Users className="h-5 w-5 text-[#39FF14]" strokeWidth={2} />
            </div>
            <div>
              <h1 className="font-display font-black text-3xl tracking-tight">
                Signup Downline
              </h1>
              <p className="text-white/50 text-sm">
                Earn <span className="text-[#39FF14] font-bold">10%</span> of every boost your signups receive — forever.
              </p>
            </div>
          </div>
        </div>

        {/* ------- Stat cards ------- */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            testid="stat-signups"
            label="Signups"
            value={totals.signup_count.toLocaleString()}
            icon={<Users className="h-4 w-4" />}
            color="#39FF14"
          />
          <StatCard
            testid="stat-active"
            label="Active earners"
            value={totals.active_earners.toLocaleString()}
            icon={<Flame className="h-4 w-4" />}
            color="#FF007F"
          />
          <StatCard
            testid="stat-commission-lifetime"
            label="Commission earned"
            value={fmtUsd(totals.commission_lifetime_cents)}
            sub={`${fmtCoins(totals.commission_lifetime_cents)} coins`}
            icon={<Coins className="h-4 w-4" />}
            color="#FFB800"
          />
          <StatCard
            testid="stat-commission-30d"
            label="Last 30 days"
            value={fmtUsd(totals.commission_last_30d_cents)}
            sub={`${fmtCoins(totals.commission_last_30d_cents)} coins`}
            icon={<TrendingUp className="h-4 w-4" />}
            color="#00FFFF"
          />
        </div>

        {/* ------- Share prompt ------- */}
        <div
          data-testid="signup-invite-cta"
          className="border border-[#39FF14]/40 bg-gradient-to-br from-[#39FF14]/8 to-transparent p-4 flex flex-col sm:flex-row sm:items-center gap-3"
        >
          <div className="flex-1 min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#39FF14] mb-1">
              // grow your downline
            </p>
            <p className="text-sm text-white/80">
              Every video you share carries your <span className="font-mono text-white">?ref=@{user.username}</span> tag.
              Anyone who signs up counts here — forever.
            </p>
          </div>
          <button
            data-testid="copy-profile-link"
            onClick={copyProfileLink}
            className="bg-[#39FF14] text-black font-bold uppercase tracking-wider text-xs px-4 py-2 hover:shadow-[0_0_20px_rgba(57,255,20,0.5)] transition-all flex items-center gap-2 whitespace-nowrap"
          >
            <Share2 className="h-4 w-4" /> Copy my link
          </button>
        </div>

        {/* ------- Leaderboard ------- */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="h-4 w-4 text-[#FFB800]" strokeWidth={2} />
            <h2 className="font-display text-lg font-bold uppercase tracking-wider">
              Leaderboard
            </h2>
            <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-white/40">
              ranked by lifetime commission
            </span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-white/40">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : signups.length === 0 ? (
            <div
              data-testid="signups-empty"
              className="border border-white/10 bg-white/[0.02] px-6 py-12 text-center"
            >
              <Users className="h-10 w-10 text-white/20 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-white/70 font-semibold mb-1">Nobody has joined yet</p>
              <p className="text-white/40 text-sm max-w-md mx-auto">
                Share any of your videos on WhatsApp, TikTok or Facebook.
                Every friend who taps SIGN IN from that link is permanently
                linked to you — and every boost their videos ever get pays
                you 10%.
              </p>
            </div>
          ) : (
            <div className="border border-white/10">
              {signups.map((s, i) => (
                <SignupRow key={s.user_id} row={s} rank={i + 1} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ testid, label, value, sub, icon, color }) {
  return (
    <div
      data-testid={testid}
      className="border border-white/10 bg-white/[0.02] p-3"
    >
      <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1">
        <span style={{ color }}>{icon}</span>
        <span>{label}</span>
      </div>
      <div className="font-display text-2xl font-black leading-none" style={{ color }}>
        {value}
      </div>
      {sub && (
        <div className="text-[10px] font-mono text-white/40 mt-1">{sub}</div>
      )}
    </div>
  );
}

function SignupRow({ row, rank }) {
  const navigate = useNavigate();
  const [dmBusy, setDmBusy] = useState(false);
  const isTop = rank === 1 && row.commission_cents > 0;

  const openDm = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dmBusy) return;
    setDmBusy(true);
    try {
      const r = await api.post("/messages/conversations", { username: row.username });
      const convId = r?.data?.id;
      if (convId) navigate(`/messages/${convId}`);
      else toast.error("Couldn't open the conversation");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to open DM");
    } finally {
      setDmBusy(false);
    }
  };

  return (
    <div
      data-testid={`signup-row-${row.username}`}
      className={`flex items-center gap-3 px-3 py-3 border-b border-white/5 last:border-b-0 ${
        isTop ? "bg-[#FFB800]/5" : ""
      }`}
    >
      <div className={`w-8 text-center font-mono text-sm font-bold ${
        rank === 1 ? "text-[#FFB800]" :
        rank === 2 ? "text-white/80" :
        rank === 3 ? "text-[#FF9500]" : "text-white/30"
      }`}>
        {rank === 1 ? <Crown className="h-4 w-4 mx-auto" /> : rank}
      </div>
      <Link
        to={`/u/${row.username}`}
        className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80"
      >
        {row.picture ? (
          <img
            src={picUrl(row.picture)}
            alt=""
            className="h-10 w-10 object-cover border border-white/10 flex-shrink-0"
          />
        ) : (
          <div className="h-10 w-10 bg-[#39FF14]/20 flex items-center justify-center text-[#39FF14] font-bold flex-shrink-0">
            {row.name?.[0]?.toUpperCase() || "?"}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-white text-sm font-semibold truncate">
            {row.name || `@${row.username}`}
          </p>
          <p className="font-mono text-[10px] uppercase tracking-wider text-white/40 truncate">
            @{row.username} · joined {fmtDate(row.joined_at)}
          </p>
        </div>
      </Link>
      <div className="text-right flex-shrink-0">
        <div className="font-display font-black text-[#39FF14] text-base leading-none">
          {fmtUsd(row.commission_cents)}
        </div>
        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-0.5">
          {row.commission_count > 0
            ? `${row.commission_count} boost${row.commission_count === 1 ? "" : "s"}`
            : "no earnings yet"}
        </div>
      </div>
      <button
        type="button"
        onClick={openDm}
        disabled={dmBusy}
        data-testid={`signup-dm-${row.username}`}
        title="Send a message"
        className="h-8 w-8 flex items-center justify-center text-white/40 hover:text-[#00FFFF] disabled:opacity-40 transition-colors flex-shrink-0"
      >
        {dmBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" strokeWidth={1.6} />}
      </button>
    </div>
  );
}
